import { RateGender } from "@platform/openapi-types";

import { coverageTierToLabelLong, formatAgeBandLabel } from "./rateFormatters";
import { currentSchedules } from "./rateSets";

import type { BenefitPlan, BenefitPlanGroup } from "../types/models";
import type { EnrollmentDecision, Rate, RateSchedule } from "../types";

/** What a review row measures: enrolled lives, or the volume a rate is priced on. */
export type EnrollmentMeasure = "lives" | "volume";

/** Measures in the fixed order totals are reported in, so results never depend on plan order. */
const ENROLLMENT_MEASURES: readonly EnrollmentMeasure[] = ["lives", "volume"];

export interface EnrollmentReviewRow {
  readonly key: string;
  readonly label: string;
  /** What this row's own rate is quoted on; a plan can mix volume-based and unit-based rates. */
  readonly measure: EnrollmentMeasure;
  readonly current: number | undefined;
  readonly carrier: number;
  /** The carrier's figure above (positive) or below (negative) Current's, in whole percent. */
  readonly differencePercent: number | undefined;
  readonly flagged: boolean;
}

export interface EnrollmentReviewOption {
  readonly planId: string;
  /** The option's label for a tab, empty when the group has a single option. */
  readonly label: string;
  readonly rows: readonly EnrollmentReviewRow[];
  readonly flagged: boolean;
}

export interface EnrollmentMeasureTotal {
  readonly measure: EnrollmentMeasure;
  /** The carrier's total above or below Current's for this measure, in whole percent, unsigned. */
  readonly differencePercent: number;
}

export interface EnrollmentReview {
  readonly groupId: string;
  readonly options: readonly EnrollmentReviewOption[];
  /** Each measure's group total across every option, lives before volume; never mixed. */
  readonly totals: readonly EnrollmentMeasureTotal[];
  /**
   * The group's headline difference: the entry of `totals` with the largest difference (lives on
   * a tie), so a group mixing lives and volume reports its biggest gap. Undefined when no
   * measure has a Current total to compare with.
   */
  readonly totalDifferencePercent: number | undefined;
  /** The measure `totalDifferencePercent` describes. */
  readonly totalDifferenceMeasure: EnrollmentMeasure | undefined;
  readonly flagged: boolean;
  readonly decision: EnrollmentDecision | undefined;
  readonly needsDecision: boolean;
}

const ADND_LABEL_PREFIX = "AD&D ";

const NO_OPTION_LABEL = "";

const measureOf = (rate: Rate): EnrollmentMeasure =>
  rate.carrierVolume !== undefined ? "volume" : "lives";

/** The carrier's figure for a rate, or undefined when the carrier quoted none. */
const carrierFigure = (rate: Rate): number | undefined =>
  rate.carrierVolume ?? rate.carrierNumberOfLives;

/** Current's figure on the same measure the carrier quoted. */
const currentFigure = (rate: Rate): number | undefined =>
  measureOf(rate) === "volume" ? rate.currentVolume : rate.currentNumberOfLives;

const differencePercent = (
  carrier: number,
  current: number | undefined,
): number | undefined =>
  current === undefined || current === 0
    ? undefined
    : Math.round(((carrier - current) / current) * 100);

const LABEL_SEPARATOR = " · ";

const TOTAL_LABEL = "Total";

const TOBACCO_LABEL = "Tobacco";

const NON_TOBACCO_LABEL = "Non-Tobacco";

const GENDER_LABELS: Readonly<Record<RateGender, string>> = {
  [RateGender.male]: "Male",
  [RateGender.female]: "Female",
  [RateGender.nonBinary]: "Non-Binary",
};

/** Names every dimension the rate carries, so no two rates of a schedule read alike. */
const rateLabel = (rate: Rate, rateCount: number): string => {
  const parts = [
    rate.coverageTierCode
      ? coverageTierToLabelLong(rate.coverageTierCode, rateCount)
      : undefined,
    rate.ageBandLower != null || rate.ageBandUpper != null
      ? formatAgeBandLabel(rate.ageBandLower, rate.ageBandUpper)
      : undefined,
    rate.gender ? GENDER_LABELS[rate.gender] : undefined,
    rate.isTobaccoRated === undefined
      ? undefined
      : rate.isTobaccoRated
        ? TOBACCO_LABEL
        : NON_TOBACCO_LABEL,
  ].filter((part): part is string => part !== undefined);

  return parts.length === 0 ? TOTAL_LABEL : parts.join(LABEL_SEPARATOR);
};

const optionLabel = (optionName: string): string =>
  optionName === "notApplicable" || optionName === ""
    ? NO_OPTION_LABEL
    : `${optionName.charAt(0).toUpperCase()}${optionName.slice(1)}`;

const rowsOf = (schedule: RateSchedule): EnrollmentReviewRow[] => {
  const rates = [...(schedule.rates ?? [])].sort(
    (a, b) => a.position - b.position,
  );
  const prefix =
    schedule.rateCoverageIndicator === "adnd" ? ADND_LABEL_PREFIX : "";

  return rates.flatMap((rate) => {
    const carrier = carrierFigure(rate);

    return carrier === undefined
      ? []
      : [
          {
            key: rate.id,
            label: `${prefix}${rateLabel(rate, rates.length)}`,
            measure: measureOf(rate),
            current: currentFigure(rate),
            carrier,
            differencePercent: differencePercent(carrier, currentFigure(rate)),
            flagged: rate.enrollmentVarianceFlagged === true,
          },
        ];
  });
};

const optionOf = (plan: BenefitPlan): EnrollmentReviewOption | undefined => {
  const schedules = currentSchedules(plan.rateSchedule);
  const rows = schedules.flatMap(rowsOf);
  const carrierQuoted = schedules
    .flatMap((schedule) => schedule.rates ?? [])
    .some((rate) => carrierFigure(rate) !== undefined);

  return !carrierQuoted
    ? undefined
    : {
        planId: plan.id,
        label: optionLabel(plan.optionName),
        rows,
        flagged: rows.some((row) => row.flagged),
      };
};

const totalFor = (
  measure: EnrollmentMeasure,
  rows: readonly EnrollmentReviewRow[],
): EnrollmentMeasureTotal | undefined => {
  const comparable = rows.filter(
    (row) => row.measure === measure && row.current !== undefined,
  );
  const current = comparable.reduce((sum, row) => sum + (row.current ?? 0), 0);
  const carrier = comparable.reduce((sum, row) => sum + row.carrier, 0);

  return current === 0
    ? undefined
    : {
        measure,
        differencePercent: Math.abs(
          Math.round(((carrier - current) / current) * 100),
        ),
      };
};

const totalsOf = (
  options: readonly EnrollmentReviewOption[],
): EnrollmentMeasureTotal[] => {
  const rows = options.flatMap((option) => option.rows);

  return ENROLLMENT_MEASURES.flatMap(
    (measure) => totalFor(measure, rows) ?? [],
  );
};

const largestTotal = (
  totals: readonly EnrollmentMeasureTotal[],
): EnrollmentMeasureTotal | undefined =>
  totals.reduce<EnrollmentMeasureTotal | undefined>(
    (largest, total) =>
      largest === undefined ||
      total.differencePercent > largest.differencePercent
        ? total
        : largest,
    undefined,
  );

/**
 * The comparison of a quoted plan group's carrier enrollment with Current's, for the review
 * dialog. Undefined when the carrier quoted no enrollment on any plan of the group.
 */
export const buildEnrollmentReview = (
  group: BenefitPlanGroup,
): EnrollmentReview | undefined => {
  const options = group.benefitPlans.flatMap((plan) => optionOf(plan) ?? []);

  if (options.length === 0) {
    return undefined;
  }

  const schedules = group.benefitPlans.flatMap((plan) =>
    currentSchedules(plan.rateSchedule),
  );

  const totals = totalsOf(options);
  const headline = largestTotal(totals);

  return {
    groupId: group.id,
    options,
    totals,
    totalDifferencePercent: headline?.differencePercent,
    totalDifferenceMeasure: headline?.measure,
    flagged: group.enrollmentGroupFlagged === true,
    decision:
      schedules.find((schedule) => schedule.enrollmentDecision)
        ?.enrollmentDecision ?? undefined,
    needsDecision: schedules.some(
      (schedule) => schedule.enrollmentNeedsDecision === true,
    ),
  };
};
